import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, MapPin, Wallet, CalendarRange, Sun, Waves, Luggage, Clock, Moon,
  ArrowRight, Check, Loader2, Minus, Plus as PlusIcon,
} from "lucide-react";
import { Shell } from "../components/Shell";
import { api, eur } from "../lib/api";
import { loadGoal, saveGoal, clearGoal, DEFAULT_GOAL, isNewObjectiveIntent } from "../lib/goal";
import { useI18n } from "../i18n";

export default function Plan() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang } = useI18n();
  const [c, setC] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [evaluating, setEvaluating] = useState(false);
  const [evalStep, setEvalStep] = useState(0);

  const PREF_OPTIONS = [
    { key: "sun", label: t("plan.prefSun"), icon: Sun },
    { key: "swimming pool", label: t("plan.prefPool"), icon: Waves },
    { key: "checked baggage", label: t("plan.prefBags"), icon: Luggage },
  ];
  const EVAL_STEPS = t("plan.evalSteps");
  const prefLabel = (k) => PREF_OPTIONS.find((o) => o.key === k)?.label || k;

  useEffect(() => {
    // NEW objective (Landing CTAs, Reset): start from a neutral goal — drop whatever
    // was previously persisted so no earlier constraints/preferences leak in. This must
    // run before the effects below, which load whatever is (still) in storage.
    if (isNewObjectiveIntent(location.state)) clearGoal();
  }, []); // eslint-disable-line

  useEffect(() => {
    // Destination picker options — the full live-search destination registry
    // (backend flight_provider.DESTINATIONS via GET /api/destinations), not just the
    // 13 controlled-demo cities. Single source of truth: the same list resolve_destination()
    // uses for live search, so a structured pick always resolves correctly downstream.
    api.destinations().then((d) => setDestinations(d.destinations || [])).catch(() => setDestinations([]));
  }, []);

  useEffect(() => {
    const stored = loadGoal();
    if (stored) { setC(stored); return; }
    // The demo seed (DEFAULT_GOAL / api.demoGoal()) is a curated 2-adult/2-child family
    // persona with real preferences + constraints baked in (sun/pool/baggage, max 6h, no
    // overnight) — correct for a first-ever visitor, but NEW must not silently treat any
    // of it as already-decided. preferences becomes [] (nothing pre-toggled);
    // max_travel_hours/no_overnight become null ("not yet an active constraint" — the
    // sliders/toggle still render a sensible position, see the JSX below, but that display
    // value only becomes real once the user moves them or states it in text); adults/
    // children reset to a neutral 1/0 baseline (a real, concrete "just me" starting point,
    // not the family persona) — so stating only "2 adults" later can never leave a stale
    // demo children count behind.
    const neutralize = (g) => (isNewObjectiveIntent(location.state)
      ? { ...g, preferences: [], max_travel_hours: null, no_overnight: null, adults: 1, children: 0 }
      : g);
    api.demoGoal().then((g) => setC(neutralize({ ...DEFAULT_GOAL, ...g }))).catch(() => setC(neutralize(DEFAULT_GOAL)));
  }, []);

  const upd = (k, v) => setC((p) => { const n = { ...p, [k]: v }; saveGoal(n); return n; });
  const updMany = (patch) => setC((p) => { const n = { ...p, ...patch }; saveGoal(n); return n; });
  const togglePref = (k) => setC((p) => {
    const has = p.preferences.includes(k);
    const n = { ...p, preferences: has ? p.preferences.filter((x) => x !== k) : [...p.preferences, k] };
    saveGoal(n);
    return n;
  });

  // Structured destination pick builds the same canonical fields the (now dormant)
  // NL parser used to derive — destination name + geography — directly, no parsing.
  const selectDestination = (value) => {
    if (value === "open") {
      updMany({ destination: "open", destination_code: null, country: null, continent: null, geography: null });
      return;
    }
    const picked = destinations.find((d) => d.name === value);
    updMany({
      destination: value, destination_code: picked?.code || null,
      country: picked?.country || null, continent: picked?.continent || null,
      geography: { scope: "destination", value, label: value, label_fr: value },
    });
  };

  // date_mode is an explicit tab, not just inferred from which fields happen to be
  // filled — switching modes must never mix fixed dates and window dates. Entering
  // flexible_window always clears departure/return so they can't linger and silently
  // flip the (server-inferred) mode back to "fixed" behind the user's back.
  const switchDateMode = (mode) => {
    if (mode === c.date_mode) return;
    if (mode === "fixed") updMany({ date_mode: "fixed" });
    else updMany({ date_mode: "flexible_window", departure_date: null, return_date: null });
  };
  const deriveFixedNights = (departure, ret) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(departure || "") || !/^\d{4}-\d{2}-\d{2}$/.test(ret || "")) return null;
    const d1 = new Date(`${departure}T12:00:00`);
    const d2 = new Date(`${ret}T12:00:00`);
    const days = Math.round((d2 - d1) / 86400000);
    return days > 0 ? days : null;
  };
  const setDate = (key, value) => {
    const nextValue = value || null;
    if (c.date_mode !== "fixed") { upd(key, nextValue); return; }
    const departure = key === "departure_date" ? nextValue : c.departure_date;
    const ret = key === "return_date" ? nextValue : c.return_date;
    const nights = deriveFixedNights(departure, ret);
    updMany({ [key]: nextValue, ...(nights != null ? { nights } : {}) });
  };
  // ISO-only, native <input type="date"> can't render the legacy 'DD Mon' demo default
  // (e.g. "15 Oct") — shows empty until the user picks a real date, rather than guessing.
  const isoOrEmpty = (s) => (/^\d{4}-\d{2}-\d{2}$/.test(s || "") ? s : "");

  const invalidFlexibleWindow = c?.date_mode === "flexible_window"
    && /^\d{4}-\d{2}-\d{2}$/.test(c.window_start || "")
    && /^\d{4}-\d{2}-\d{2}$/.test(c.window_end || "")
    && c.window_end <= c.window_start;

  const findTrips = () => {
    if (invalidFlexibleWindow) return;
    saveGoal(c);
    sessionStorage.setItem("dv_budget", String(c.budget));
    // This transition is deliberately qualitative. The actual search happens on Results;
    // showing numerical "analysis" counts here would imply provider work that has not yet
    // happened. Real execution counts are surfaced only after the Results search settles.
    setEvaluating(true); setEvalStep(0);
    const total = EVAL_STEPS.length;
    const iv = setInterval(() => {
      setEvalStep((s) => {
        if (s >= total - 1) { clearInterval(iv); setTimeout(() => navigate("/results"), 750); return s; }
        return s + 1;
      });
    }, 360);
  };

  if (!c) return <Shell active="plan"><PlanSkeleton /></Shell>;

  return (
    <Shell active="plan">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.24em] text-slate-400 font-display">
          <span style={{ color: "var(--brand)" }}>{t("plan.kicker")}</span>
        </div>
        <h1 className="mt-2 font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{t("plan.title")}</h1>
        <p className="mt-2 text-slate-500 text-sm max-w-xl">{t("plan.sub")}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field icon={Users} label={t("plan.travellers")}>
          <div className="flex items-center gap-4">
            <Stepper label={t("plan.adults")} value={c.adults} onChange={(v) => upd("adults", v)} min={1} testid="adults" />
            <Stepper label={t("plan.children")} value={c.children} onChange={(v) => upd("children", v)} min={0} testid="children" />
          </div>
        </Field>

        <Field icon={MapPin} label={t("plan.departureDest")}>
          <div className="flex items-center gap-2">
            <input value={c.departure} onChange={(e) => upd("departure", e.target.value)} data-testid="departure-input" className="w-1/2 rounded-xl border border-[var(--line-strong)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
            <select value={c.destination && c.destination !== "open" ? c.destination : "open"} onChange={(e) => selectDestination(e.target.value)}
              data-testid="destination-select"
              className="w-1/2 rounded-xl border border-[var(--line-strong)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)] bg-white">
              <option value="open">{t("plan.surpriseMe")}</option>
              {destinations.map((d) => <option key={d.code} value={d.name}>{d.name}</option>)}
            </select>
          </div>
        </Field>

        <Field icon={Wallet} label={t("plan.budgetTitle")}>
          <div>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="dv-total text-3xl text-slate-900">{eur(c.budget)}</span>
              <span className="text-xs text-slate-400">{t("plan.budgetHelper")}</span>
            </div>
            <input type="range" min={1200} max={4000} step={50} value={c.budget} onChange={(e) => upd("budget", Number(e.target.value))} data-testid="budget-slider" className="w-full mt-3 accent-[var(--brand)]" />
          </div>
        </Field>

        <Field icon={CalendarRange} label={t("plan.windowDuration")} full>
          <div className="flex items-center gap-2 mb-3">
            <button onClick={() => switchDateMode("flexible_window")} data-testid="date-mode-flexible-window"
              className="dv-pill text-xs px-3 py-1.5"
              style={{ background: c.date_mode !== "fixed" ? "var(--ink)" : "var(--paper)", color: c.date_mode !== "fixed" ? "#fff" : "var(--ink-soft)", border: `1px solid ${c.date_mode !== "fixed" ? "var(--ink)" : "var(--line)"}` }}>
              {t("plan.flexibleWindowTab")}
            </button>
            <button onClick={() => switchDateMode("fixed")} data-testid="date-mode-fixed"
              className="dv-pill text-xs px-3 py-1.5"
              style={{ background: c.date_mode === "fixed" ? "var(--ink)" : "var(--paper)", color: c.date_mode === "fixed" ? "#fff" : "var(--ink-soft)", border: `1px solid ${c.date_mode === "fixed" ? "var(--ink)" : "var(--line)"}` }}>
              {t("plan.fixedDatesTab")}
            </button>
          </div>

          {c.date_mode === "fixed" ? (
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <div className="text-[11px] text-slate-400 mb-1">{t("plan.departureDateLabel")}</div>
                <input type="date" value={c.departure_date || ""} onChange={(e) => setDate("departure_date", e.target.value)}
                  data-testid="departure-date-input" className="rounded-xl border border-[var(--line-strong)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
              </div>
              <ArrowRight size={14} className="text-slate-300 mb-3" />
              <div>
                <div className="text-[11px] text-slate-400 mb-1">{t("plan.returnDateLabel")}</div>
                <input type="date" min={c.departure_date || undefined} value={c.return_date || ""} onChange={(e) => setDate("return_date", e.target.value)}
                  data-testid="return-date-input" className="rounded-xl border border-[var(--line-strong)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
              </div>
              <div>
                <div className="text-[11px] text-slate-400 mb-1">{t("plan.nights")}</div>
                <div className="h-[42px] min-w-[74px] rounded-xl border border-[var(--line)] bg-[var(--paper)] px-3 flex items-center justify-center">
                  <span className="dv-total text-lg" data-testid="fixed-nights-value">{c.nights}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <div className="text-[11px] text-slate-400 mb-1">{t("plan.windowStartLabel")}</div>
                <input type="date" value={isoOrEmpty(c.window_start)} onChange={(e) => setDate("window_start", e.target.value)}
                  data-testid="window-start-input" className="rounded-xl border border-[var(--line-strong)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
              </div>
              <ArrowRight size={14} className="text-slate-300 mb-3" />
              <div>
                <div className="text-[11px] text-slate-400 mb-1">{t("plan.windowEndLabel")}</div>
                <input type="date" min={isoOrEmpty(c.window_start) || undefined} value={isoOrEmpty(c.window_end)} onChange={(e) => setDate("window_end", e.target.value)}
                  data-testid="window-end-input" className="rounded-xl border border-[var(--line-strong)] px-3 py-2.5 text-sm outline-none focus:border-[var(--brand)]" />
              </div>
              <Stepper label={t("plan.nights")} value={c.nights} onChange={(v) => upd("nights", v)} min={1} testid="nights" />
            </div>
          )}
          <div className="text-xs text-slate-500 mt-2" data-testid="dates-mode-note">
            {c.date_mode === "fixed" ? t("plan.fixedDatesNote") : t("plan.flexibleWindowNote")}
          </div>
        </Field>

        <Field icon={Sun} label={t("plan.preferences")} full>
          <div className="flex flex-wrap gap-2">
            {PREF_OPTIONS.map((p) => {
              const on = c.preferences.includes(p.key);
              return (
                <button key={p.key} onClick={() => togglePref(p.key)} data-testid={`pref-${p.key.replace(/\s+/g, "-")}`}
                  className="dv-pill text-sm px-3.5 py-2 transition-all"
                  style={{ background: on ? "var(--ink)" : "var(--paper)", color: on ? "#fff" : "var(--ink-soft)", border: `1px solid ${on ? "var(--ink)" : "var(--line)"}` }}>
                  <p.icon size={15} /> {p.label} {on && <Check size={13} />}
                </button>
              );
            })}
          </div>
        </Field>

        <Field icon={Clock} label={t("plan.maxTravel")}>
          <div className="flex items-center gap-3">
            {/* c.max_travel_hours may be null (NEW, untouched) — the slider still shows a
                sensible position, but that number only becomes a real constraint once the
                user actually moves it (upd() then replaces null with a concrete value). */}
            <span className="dv-total text-2xl text-slate-900">{c.max_travel_hours == null ? t("plan.notSet") : `${c.max_travel_hours}h`}</span>
            <input type="range" min={2} max={24} step={1} value={c.max_travel_hours ?? 6} onChange={(e) => upd("max_travel_hours", Number(e.target.value))} data-testid="travel-time-slider" className="flex-1 accent-[var(--brand)]" style={{ opacity: c.max_travel_hours == null ? 0.55 : 1 }} />
            {c.max_travel_hours == null && (
              <button type="button" onClick={() => upd("max_travel_hours", 6)} data-testid="travel-time-enable"
                className="dv-pill text-[11px] px-2.5 py-1" style={{ background: "var(--brand-soft)", color: "var(--brand)" }}>
                {t("plan.enableTravelTime")}
              </button>
            )}
          </div>
        </Field>

        <Field icon={Moon} label={t("plan.overnight")}>
          {/* Same null-until-touched handling as above, via a display fallback. */}
          <button onClick={() => upd("no_overnight", c.no_overnight == null ? true : !c.no_overnight)} data-testid="overnight-toggle"
            className="w-full flex items-center justify-between rounded-xl px-4 py-3 transition-colors"
            style={{ background: c.no_overnight === true ? "var(--book-soft)" : "var(--paper)", border: "1px solid var(--line)" }}>
            <span className="text-sm font-medium text-slate-700">{c.no_overnight == null ? t("plan.notSet") : c.no_overnight ? t("plan.noOvernight") : t("plan.overnightAllowed")}</span>
            <span className="w-11 h-6 rounded-full relative transition-colors" style={{ background: c.no_overnight === true ? "var(--book)" : "var(--line-strong)" }}>
              <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all" style={{ left: c.no_overnight === true ? "22px" : "2px" }} />
            </span>
          </button>
        </Field>
      </div>

      <div className="mt-6" data-testid="goal-recap">
        <div className="dv-card p-4 sm:p-5" style={{ background: "var(--paper)" }}>
          <div className="text-[11px] font-semibold tracking-[0.18em] text-slate-400 font-display mb-3">{t("plan.recapTitle")}</div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
            <span className="text-[11px] font-semibold text-slate-500 mr-1">{t("plan.recapHard")}:</span>
            {c.geography && (
              <span className="dv-pill text-xs px-2.5 py-1" data-testid="recap-geography" style={{ background: "var(--ink)", color: "#fff" }}>
                <MapPin size={12} /> {lang === "fr" ? (c.geography.label_fr || c.geography.label) : c.geography.label}
              </span>
            )}
            <span className="dv-pill text-xs px-2.5 py-1" style={{ background: "#fff", border: "1px solid var(--line)" }}><Wallet size={12} color="var(--brand)" /> ≤ {eur(c.budget)}</span>
            <span className="dv-pill text-xs px-2.5 py-1" style={{ background: "#fff", border: "1px solid var(--line)" }}><CalendarRange size={12} color="var(--brand)" /> {c.departure_date && c.return_date ? `${c.departure_date} → ${c.return_date}` : t("plan.recapNights", { n: c.nights })}</span>
            <span className="dv-pill text-xs px-2.5 py-1" style={{ background: "#fff", border: "1px solid var(--line)" }}><Users size={12} color="var(--brand)" /> {t("plan.recapTravellers", { a: c.adults, c: c.children })}</span>
            {/* Recap must reflect what will actually be enforced — an unset (NEW,
                untouched) limit is not a real constraint yet, so it's simply not shown. */}
            {c.max_travel_hours != null && (
              <span className="dv-pill text-xs px-2.5 py-1" style={{ background: "#fff", border: "1px solid var(--line)" }}><Clock size={12} color="var(--brand)" /> ≤ {c.max_travel_hours}h</span>
            )}
          </div>
          {c.preferences?.length > 0 && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-2 mt-3">
              <span className="text-[11px] font-semibold text-slate-500 mr-1">{t("plan.recapPrefs")}:</span>
              {c.preferences.map((p) => (
                <span key={p} className="dv-pill text-xs px-2.5 py-1" style={{ background: "var(--brand-soft)", color: "var(--brand)" }}>{prefLabel(p)}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5 pt-2">
        {invalidFlexibleWindow && <div className="mb-3 text-sm font-medium" style={{ color: "var(--alert)" }} data-testid="invalid-window-inline">{t("plan.invalidWindowOrder")}</div>}
        <button onClick={findTrips} disabled={invalidFlexibleWindow} data-testid="find-trips-button" className="dv-btn dv-btn-primary w-full text-base py-4" style={{ opacity: invalidFlexibleWindow ? 0.55 : 1 }}>
          {t("plan.findTrips")} <ArrowRight size={18} />
        </button>
      </div>

      <AnimatePresence>{evaluating && <Evaluating step={evalStep} steps={EVAL_STEPS} constraints={c} t={t} />}</AnimatePresence>
    </Shell>
  );
}

const Field = ({ icon: Icon, label, children, full }) => (
  <div className={`dv-card p-4 sm:p-5 ${full ? "sm:col-span-2" : ""}`}>
    <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 mb-3"><Icon size={15} color="var(--brand)" /> {label}</div>
    {children}
  </div>
);

const Stepper = ({ label, value, onChange, min = 0, testid }) => (
  <div>
    <div className="text-[11px] text-slate-400 mb-1">{label}</div>
    <div className="flex items-center gap-2">
      <button onClick={() => onChange(Math.max(min, value - 1))} data-testid={`${testid}-minus`} className="w-8 h-8 rounded-full border border-[var(--line-strong)] flex items-center justify-center active:scale-90 transition-transform"><Minus size={14} /></button>
      <span className="dv-total text-lg w-6 text-center" data-testid={`${testid}-value`}>{value}</span>
      <button onClick={() => onChange(value + 1)} data-testid={`${testid}-plus`} className="w-8 h-8 rounded-full border border-[var(--line-strong)] flex items-center justify-center active:scale-90 transition-transform"><PlusIcon size={14} /></button>
    </div>
  </div>
);

const Evaluating = ({ step, steps, constraints, t }) => {
  const lines = steps;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center px-6" style={{ background: "rgba(15,23,42,0.72)", backdropFilter: "blur(6px)" }}>
      <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} className="dv-card w-full max-w-md p-7" data-testid="evaluating-overlay">
        <div className="flex items-center gap-3">
          <div className="relative w-11 h-11 rounded-2xl flex items-center justify-center dv-pulse" style={{ background: "var(--ink)" }}><Loader2 size={20} color="#fff" className="animate-spin" /></div>
          <div>
            <div className="font-display font-bold text-lg leading-tight">{t("plan.analysisTitle")}</div>
            <div className="text-xs text-slate-500">{t("plan.evalSub", { b: eur(constraints.budget), n: constraints.nights, d: constraints.departure })}</div>
          </div>
        </div>
        <div className="mt-6 space-y-2.5">
          {lines.map((s, i) => {
            const done = i < step, active = i === step, last = i === lines.length - 1;
            return (
              <div key={i} className="flex items-center gap-3 text-sm transition-opacity" style={{ opacity: i <= step ? 1 : 0.4 }}>
                <span className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: done || (last && active) ? "var(--book)" : active ? "var(--brand-soft)" : "var(--paper)", border: `1px solid ${done || (last && active) ? "var(--book)" : "var(--line)"}` }}>
                  {done || (last && active) ? <Check size={12} color="#fff" /> : active ? <Loader2 size={11} className="animate-spin" color="var(--brand)" /> : null}
                </span>
                <span style={{ color: i <= step ? "var(--ink)" : "var(--muted)", fontWeight: active || last ? 600 : 500 }}>{s}</span>
              </div>
            );
          })}
        </div>
        <div className="mt-5 text-xs text-slate-500 border-t border-[var(--line)] pt-4">{t("plan.analysisSub")}</div>
      </motion.div>
    </motion.div>
  );
};

const PlanSkeleton = () => (
  <div className="grid sm:grid-cols-2 gap-4">{Array.from({ length: 6 }).map((_, i) => <div key={i} className="dv-skeleton h-32 rounded-2xl" />)}</div>
);
